import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DotsVerticalIcon } from './icons';

export interface ContextMenuItem {
  icon?: React.ReactNode;
  label: string;
  /** Renders the item in a danger (red) style */
  danger?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export interface ContextMenuProps {
  items: ContextMenuItem[];
  /** aria-label for the ⋮ trigger button */
  triggerLabel?: string;
}

interface MenuPosition {
  top: number;
  left?: number;
  right?: number;
}

/** Generic ⋮ vertical 3-dots dropdown menu. Renders nothing when items is empty. */
export function ContextMenu({ items, triggerLabel }: ContextMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => setOpen(false), []);

  function openMenu() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const isRTL = document.documentElement.dir === 'rtl';
    setMenuPos(
      isRTL
        ? { top: rect.bottom + 4, left: rect.left }
        : { top: rect.bottom + 4, right: window.innerWidth - rect.right }
    );
    setOpen(true);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (!triggerRef.current?.contains(e.target as Node) && !menuRef.current?.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, close]);

  // Close on scroll or resize (menu position becomes stale)
  useEffect(() => {
    if (!open) return;
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  // Focus first item when menu opens
  useEffect(() => {
    if (open) {
      const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
      firstItem?.focus();
    }
  }, [open]);

  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel ?? t('contextMenu.trigger')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => (open ? close() : openMenu())}
        className="flex items-center justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
      >
        <span aria-hidden="true" className="inline-flex h-5 w-5 shrink-0">
          <DotsVerticalIcon />
        </span>
      </button>

      {open && menuPos
        && createPortal(
          <ul
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: menuPos.top,
              ...(menuPos.right !== undefined ? { right: menuPos.right } : { left: menuPos.left }),
            }}
            className="z-50 min-w-[140px] rounded-md border border-gray-200 bg-white py-1 shadow-md"
          >
            {items.map((item) => (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    close();
                    item.onClick();
                  }}
                  className={[
                    'flex w-full items-center gap-2 px-3 py-1.5 text-start font-body text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-600',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    item.danger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-gray-700 hover:bg-gray-50',
                  ].join(' ')}
                >
                  {item.icon && (
                    <span aria-hidden="true" className="inline-flex h-4 w-4 shrink-0">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
}
